import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, TrendingUp, Shield, Target } from 'lucide-react';
import { toast } from 'sonner';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: ''
  });
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Connexion réussie !');
      } else {
        if (!formData.username) {
          toast.error('Le nom d\'utilisateur est requis');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.username);
        toast.success('Compte créé avec succès !');
      }
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: TrendingUp, title: 'Suivi Patrimoine', desc: 'Visualisez votre patrimoine net en temps réel' },
    { icon: Shield, title: 'Profil Investisseur', desc: 'Découvrez votre ADN d\'investisseur' },
    { icon: Target, title: 'Objectifs', desc: 'Atteignez vos objectifs financiers' }
  ];

  return (
    <div className="min-h-screen bg-cap-bg flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cap-emerald/10 via-transparent to-cap-blue/10" />
        <div className="relative z-10 flex flex-col justify-center p-12 xl:p-20">
          <div className="mb-12">
            <h1 className="font-display text-5xl xl:text-6xl font-bold text-white mb-4 tracking-tight">
              CAP <span className="text-cap-emerald">OS</span>
            </h1>
            <p className="text-xl text-cap-text-secondary">
              Votre cockpit financier personnel
            </p>
          </div>
          
          <div className="space-y-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="p-3 rounded-xl bg-cap-bg-elevated border border-white/5">
                  <feature.icon className="w-6 h-6 text-cap-emerald" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-white mb-1">{feature.title}</h3>
                  <p className="text-cap-text-secondary text-sm">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cap-emerald/5 rounded-full blur-3xl" />
        <div className="absolute top-20 right-0 w-64 h-64 bg-cap-blue/5 rounded-full blur-3xl" />
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="font-display text-4xl font-bold text-white">
              CAP <span className="text-cap-emerald">OS</span>
            </h1>
          </div>

          <div className="glass-card p-8">
            <div className="text-center mb-8">
              <h2 className="font-display text-2xl font-semibold text-white mb-2">
                {isLogin ? 'Connexion' : 'Créer un compte'}
              </h2>
              <p className="text-cap-text-secondary">
                {isLogin 
                  ? 'Accédez à votre espace personnel'
                  : 'Rejoignez la communauté CAP OS'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-cap-text-secondary">
                    Nom d'utilisateur
                  </Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Votre pseudo"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="bg-cap-bg border-white/10 text-white placeholder:text-cap-text-muted focus:border-cap-blue h-12"
                    data-testid="auth-username-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-cap-text-secondary">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-cap-bg border-white/10 text-white placeholder:text-cap-text-muted focus:border-cap-blue h-12"
                  required
                  data-testid="auth-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-cap-text-secondary">
                  Mot de passe
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-cap-bg border-white/10 text-white placeholder:text-cap-text-muted focus:border-cap-blue h-12"
                  required
                  minLength={6}
                  data-testid="auth-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover shadow-glow-emerald hover:shadow-glow-emerald-hover transition-all"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  isLogin ? 'Se connecter' : 'Créer mon compte'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-cap-text-secondary hover:text-cap-emerald transition-colors text-sm"
                data-testid="auth-toggle-btn"
              >
                {isLogin 
                  ? "Pas encore de compte ? S'inscrire"
                  : 'Déjà un compte ? Se connecter'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
